/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { useEffect, useState } from 'react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Company } from 'types/entities/Company'
import { setHeadbarTitle } from 'helpers/actions'
import { useTypedSelector } from 'helpers/reducers'
import { removeSelectedCompanyContact } from 'store/companies/CompaniesReducer'
import CompanyAddressForm from 'components/Companies/forms/CompanyAddressForm'
import CompanyContactForm from 'components/Companies/forms/CompanyContactForm'
import { CompanyContact } from 'types/entities/CompanyContact'
import { CompaniesAction } from 'store/companies/CompaniesAction'
import { ContactsAction } from 'store/contacts/ContactsAction'
import { CompanyAddress } from 'types/entities/CompanyAddress'
import { toast } from 'utils/toastUtils'
import UiFormModalShell from 'components/Forms/UiFormModalShell'
import clsx from 'clsx'
import UiTabSwitcher, { UiTabSwitcherItem } from 'components/Ui/UiTabSwitcher'
import CreateOfferModal from 'components/Offers/CreateOfferModal'
import { useAppDispatch } from 'helpers/store'
import { useHeadbarReminders } from 'utils/hooks/useHeadbarReminders'
import EditCompanyModalInlineOverlay from './EditCompanyModalComponents/EditCompanyModalInlineOverlay'
import { useEditCompanyTabs } from './EditCompanyModalUtils'
import EditCompanyModalCompanyTab from './EditCompanyModalComponents/EditCompanyModalCompanyTab'
import EditCompanyModalLeadsTab from './EditCompanyModalComponents/EditCompanyModalLeadsTab'
import EditCompanyModalOpportunitiesTab from './EditCompanyModalComponents/EditCompanyModalOpportunitiesTab'
import EditCompanyModalOffersTab from './EditCompanyModalComponents/EditCompanyModalOffersTab'
import EditCompanyModalReservationsTab from './EditCompanyModalComponents/EditCompanyModalReservationsTab'
import EditCompanyModalActivityTab from './EditCompanyModalComponents/EditCompanyModalActivityTab'
import { getCompanyDisplayName } from 'utils/companyUtils'
import { UiTabVariant } from 'types/enums/UiTabVariant'
import { EditCompanyModalTab } from 'types/enums/specific/EditCompanyModalTab'
import { useRefreshSelectedCompany } from './hooks/useRefreshSelectedCompany'
import { splitLeadsAndOpportunities } from 'utils/leadOpportunityUtils'
import UiCrmLifecycleStatusPill from 'components/Ui/UiCrmLifecycleStatusPill'
import UiLoader from 'components/Ui/UiLoader'
import { resolveCompanyLifecycleStatus } from 'utils/crmLifecycleStatusUtils'
import { useEditCompanyActions } from './hooks/useEditCompanyActions'
import { ReminderModelName } from 'types/enums/ReminderModelName'

interface EditCompanyModalProps {
    onDirtyChange?: (dirty: boolean) => void
    company: Company
    onSuccess?: (company: Company) => void
    isSecondary?: boolean
    embedded?: boolean
}

const EditCompanyModal = (props: EditCompanyModalProps) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const selectedCompany = useTypedSelector(
        (state) => state.companiesState.selectedCompany
    )
    const secondaryOpen = useTypedSelector(state => !!state.ui?.secondaryModal?.isOpen)
    const headbarTitle = useTypedSelector(state => state.ui?.headbarTitle)

    useHeadbarReminders(true, ReminderModelName.Company, props.company.id)

    const [editingAddress, setEditingAddress] = useState<CompanyAddress | undefined>(undefined)
    const [editingContact, setEditingContact] = useState<CompanyContact | undefined>(undefined)
    const [showAddressForm, setShowAddressForm] = useState(false)
    const [showContactForm, setShowContactForm] = useState(false)
    const [loadedCompanyId, setLoadedCompanyId] = useState<number | null>(null)

    const refreshSelectedCompany = useRefreshSelectedCompany(selectedCompany?.id)

    const {
        activeTab,
        handleTabClick,
        handleCreateLead,
        handleCreateOffer,
        handleLeadClick,
        handleOfferClick,
        handleReservationClick,
        handleViewContact,
        onChangeCompanyHandler,
        handleOfferCreated,
        handleOfferGuardStateChange,
    } = useEditCompanyActions({
        selectedCompany,
        isSecondary: props.isSecondary,
        onSuccess: props.onSuccess,
        refreshSelectedCompany,
    })

    const handleAddAddress = () => {
        setEditingAddress(undefined)
        setShowAddressForm(true)
    }

    const handleAddContact = () => {
        setEditingContact(undefined)
        setShowContactForm(true)
    }

    const handleEditAddress = (address?: CompanyAddress) => {
        setEditingAddress(address)
        setShowAddressForm(true)
    }

    const handleEditContact = (contact?: CompanyContact) => {
        setEditingContact(contact)
        setShowContactForm(true)
    }

    const handleAddressFormClose = () => {
        setShowAddressForm(false)
        setEditingAddress(undefined)
    }

    const handleContactFormClose = () => {
        setShowContactForm(false)
        setEditingContact(undefined)
    }

    const handleAddressFormSuccess = async () => {
        await refreshSelectedCompany()
        handleAddressFormClose()
    }

    const handleContactFormSuccess = async () => {
        await refreshSelectedCompany()
        handleContactFormClose()
    }

    const tabs: UiTabSwitcherItem[] = useEditCompanyTabs(selectedCompany)
    const companyLeadsSplit = splitLeadsAndOpportunities(selectedCompany?.leads || [])

    const handleDeleteContact = async (contact: CompanyContact) => {
        if (selectedCompany) {
            try {
                await dispatch(ContactsAction.fetchUnassignFromCompany(contact.id, selectedCompany.id))
                dispatch(removeSelectedCompanyContact({ contactId: contact.id }))
                toast.success(t('Contact unassigned successfully'))
            } catch {
                toast.error(t('Failed to unassign contact'))
            }
        }
    }

    const handleDeleteAddress = async (address: CompanyAddress) => {
        try {
            await dispatch(CompaniesAction.fetchDeleteAddress(address.id))
            toast.success(t('Address deleted successfully'))
        } catch {
            toast.error(t('Failed to delete address'))
        }
    }

    useEffect(() => {
        let active = true
        if (props.company?.id) {
            dispatch(CompaniesAction.fetchCompanyById(props.company.id)).catch(() => {
                if (active) toast.error(t('Failed to load company details'))
            }).finally(() => {
                if (active) setLoadedCompanyId(props.company.id)
            })
        }
        return () => { active = false }
    }, [dispatch, props.company?.id, t])

    useEffect(() => {
        if (secondaryOpen && !props.isSecondary) return
        dispatch(setHeadbarTitle(t('Edit Company')))
    }, [dispatch, t, secondaryOpen, props.isSecondary])

    useEffect(() => () => { dispatch(setHeadbarTitle(null)) }, [dispatch])

    useEffect(() => {
        if (props.isSecondary || secondaryOpen || headbarTitle ||
            activeTab === EditCompanyModalTab.CreateOffer) return
        dispatch(setHeadbarTitle(t('Edit Company')))
    }, [dispatch, t, secondaryOpen, props.isSecondary, headbarTitle, activeTab])

    if (!selectedCompany || selectedCompany.id !== props.company.id) {
        if (loadedCompanyId !== props.company.id) return <UiLoader variant="page" />
        return <div className="EditCompanyModalEmpty">{t('Company not loaded')}</div>
    }

    return (
        <UiFormModalShell
            className="EditCompanyModal"
            title={t('Edit Company')}
            subtitle={getCompanyDisplayName(props.company)}
            showHeader={false}
            showHeaderCompact={!props.embedded}
            headerActions={
                !props.embedded ? (
                    <UiCrmLifecycleStatusPill status={resolveCompanyLifecycleStatus(selectedCompany)} />
                ) : undefined
            }
            headerBottom={
                !props.embedded ? (
                    <UiTabSwitcher
                        items={tabs}
                        variant={UiTabVariant.Underline}
                        selected={activeTab}
                        onClick={(tabId) => handleTabClick(tabId as EditCompanyModalTab)}
                    />
                ) : undefined
            }
            bodyClassName={clsx(
                'EditCompanyModalBody text-sm',
                !props.embedded && 'px-6',
                activeTab === EditCompanyModalTab.Company
                    ? 'grid grid-cols-12 gap-3 pb-6'
                    : 'flex flex-col h-full overflow-hidden! pb-6'
            )}
        >
            <div className={activeTab === EditCompanyModalTab.Company ? 'contents' : 'hidden'}>
                <EditCompanyModalCompanyTab
                    onDirtyChange={props.onDirtyChange}
                    selectedCompany={selectedCompany}
                    onChangeCompany={onChangeCompanyHandler}
                    onAddAddress={handleAddAddress}
                    onAddContact={handleAddContact}
                    onEditAddress={handleEditAddress}
                    onDeleteAddress={handleDeleteAddress}
                    onEditContact={handleEditContact}
                    onDeleteContact={handleDeleteContact}
                    onViewContact={handleViewContact}
                />
            </div>

            {activeTab === EditCompanyModalTab.Leads && (
                <EditCompanyModalLeadsTab
                    leads={companyLeadsSplit.leads}
                    onCreateLead={handleCreateLead}
                    onLeadClick={handleLeadClick}
                />
            )}

            {activeTab === EditCompanyModalTab.Opportunities && (
                <EditCompanyModalOpportunitiesTab
                    opportunities={companyLeadsSplit.opportunities}
                    onSuccess={refreshSelectedCompany}
                />
            )}

            {activeTab === EditCompanyModalTab.Offers && (
                <EditCompanyModalOffersTab
                    offers={selectedCompany.offers || []}
                    onCreateOffer={handleCreateOffer}
                    onOfferClick={handleOfferClick}
                />
            )}

            {activeTab === EditCompanyModalTab.Reservations && (
                <EditCompanyModalReservationsTab
                    reservations={selectedCompany.reservations || []}
                    onReservationClick={handleReservationClick}
                />
            )}

            {activeTab === EditCompanyModalTab.ActivityLog && (
                <EditCompanyModalActivityTab company={selectedCompany} />
            )}

            {activeTab === EditCompanyModalTab.CreateOffer && (
                <div className="EditCompanyModalCreateOfferTab flex-1 flex flex-col pt-4 overflow-hidden">
                    <CreateOfferModal
                        companyId={selectedCompany.id}
                        company={selectedCompany}
                        embedded={true}
                        disableCompanyChange={true}
                        onGuardStateChange={handleOfferGuardStateChange}
                        onCreated={handleOfferCreated}
                    />
                </div>
            )}

            <EditCompanyModalInlineOverlay
                isOpen={showAddressForm}
                title={t('Address Information')}
                onClose={handleAddressFormClose}
            >
                <CompanyAddressForm
                    address={editingAddress}
                    onChange={handleAddressFormSuccess}
                    showSaveAndClose={true}
                    inline={true}
                />
            </EditCompanyModalInlineOverlay>

            <EditCompanyModalInlineOverlay
                isOpen={showContactForm}
                title={t('Contact Information')}
                onClose={handleContactFormClose}
            >
                <CompanyContactForm
                    contact={editingContact}
                    onChange={handleContactFormSuccess}
                    selectFromExisting={!editingContact?.id}
                    showSaveAndClose={true}
                    inline={true}
                />
            </EditCompanyModalInlineOverlay>
        </UiFormModalShell>
    )
}

export default EditCompanyModal
